import { useMutation } from '@apollo/client/react';
import { Grid, Button, Avatar } from '@mui/material';
import DOMPurify from 'dompurify';
import React, { useState, useContext } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  type TaskComment,
  type TaskCommentInput,
  CreateTaskCommentDocument,
  UpdateTaskCommentDocument,
} from '@/graphql/generated/graphql';
import TextEditor from '@/TextEditor';
import { displayNotification } from '@/utils/notifications';

interface NewCommentProps {
  taskId: string;
  selectedComment?: TaskComment;
  setSelectedComment?: React.Dispatch<React.SetStateAction<TaskComment | null>>;
  parentCommentId?: string;
  setParentCommentId?: React.Dispatch<React.SetStateAction<string | null>>;
}

const DEFAULT_COMMENT_STATE = {
  content: '',
  isEdited: false,
  userId: '',
  taskId: '',
};

const NewComment: React.FC<NewCommentProps> = ({
  taskId,
  selectedComment,
  setSelectedComment,
  parentCommentId,
  setParentCommentId,
}) => {
  const { currentUser } = useContext(CurrentUserContext);

  const [commentData, setCommentData] = useState<TaskCommentInput>({
    ...DEFAULT_COMMENT_STATE,
    ...(selectedComment && {
      content: selectedComment.content,
      isEdited: selectedComment.isEdited,
      taskId: taskId,
      userId: selectedComment.employment.employee?.id,
    }),
  });

  const [commentText, setCommentText] = useState<string>(commentData.content);
  const [clearTextField, setClearTextField] = useState(false);

  const [createComment] = useMutation(CreateTaskCommentDocument, {
    refetchQueries: ['getTaskComments'],
    onCompleted: () => {
      displayNotification({ type: 'success', text: 'Commented successfully!' });
    },
    onError: (error) => {
      displayNotification({ type: 'error', text: error.message });
    },
  });

  const [updateComment] = useMutation(UpdateTaskCommentDocument, {
    refetchQueries: ['getTaskComments'],
    onCompleted: () => {
      displayNotification({ type: 'success', text: 'Comment updated successfully!' });
    },
    onError: (error) => {
      displayNotification({ type: 'error', text: error.message });
    },
  });

  const handleSaveComment = () => {
    setClearTextField((prev) => !prev);
    if (setParentCommentId) {
      setParentCommentId(null);
    }

    const comment = DOMPurify.sanitize(commentText);

    if (selectedComment) {
      updateComment({
        variables: {
          id: selectedComment.id,
          content: comment,
          isEdited: true,
        },
      });

      if (setSelectedComment) setSelectedComment(null);
    } else {
      createComment({
        variables: {
          comment: {
            ...commentData,
            content: comment,
            taskId: taskId,
            userId: currentUser?.id as string,
            parentCommentId,
          },
        },
      });
    }

    setCommentData(DEFAULT_COMMENT_STATE);
    setCommentText('');
  };

  const handleCancelButton = () => {
    setCommentData((prev) => ({
      ...prev,
      content: '',
    }));
    setCommentText('');
    setClearTextField((prev) => !prev);
    if (setParentCommentId) {
      setParentCommentId(null);
    }
    if (selectedComment && setSelectedComment) {
      setSelectedComment(null);
    }
  };

  return (
    <Grid container size={12} justifyContent="space-between">
      <Grid container justifyContent="space-between">
        <Avatar src={currentUser?.imageUrl as string} sx={{ width: 35, height: 35 }} />
      </Grid>
      <Grid size={11}>
        <TextEditor
          setText={(val: string) => setCommentText(val)}
          saveOn="update"
          text={commentData.content}
          options="User"
          renderToolbar
          mentionChar="@"
          key={`comment-${clearTextField}`}
        />
        {(commentText || selectedComment) && (
          <Grid container spacing={1}>
            <Button
              size="small"
              variant="contained"
              sx={{ mt: 1 }}
              disabled={!commentText}
              onClick={() => handleSaveComment()}
            >
              Save
            </Button>
            <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => handleCancelButton()}>
              Cancel
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default NewComment;
